import { IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { faRightFromBracket } from "@fortawesome/pro-solid-svg-icons";
import React from "react";

import { Button, ButtonVariant } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { OrgLogo } from "src/base-components/OrgLogo";
import { Avatar } from "src/design-system/Avatar";
import { DashboardContent } from "src/layout/DashboardContent";
import { HEADER_HEIGHT } from "src/layout/DetailHeader";
import { PickWorkspace } from "src/router/PickWorkspace";
import { getSignUpUrlWithNext } from "src/router/urls";
import { useAuthActions, useUserData } from "src/store/AuthStore";
import { useWorkspaceAndOrg } from "src/utils/useCurrentWorkspace";

type Props = {
  title?: React.ReactNode;
  buttonProps?: {
    title: React.ReactNode;
    variant?: ButtonVariant;
    icon?: IconDefinition;
    dataLoc?: string;
    onClick?: () => void;
  };
};

const OverviewHeader: React.FC<Props> = ({ title, buttonProps }) => {
  return (
    <div
      className="flex w-full flex-row items-center justify-between bg-white px-8 text-gray-800 shadow-sm"
      style={{ height: HEADER_HEIGHT }}
    >
      <div className="text-gray-800 font-inter-semibold-14px">{title}</div>
      {buttonProps?.title && (
        <Button
          dataLoc={buttonProps.dataLoc}
          iconLeft={buttonProps.icon}
          size="base"
          variant={buttonProps.variant ?? "primary"}
          onClick={buttonProps?.onClick}
        >
          {buttonProps.title}
        </Button>
      )}
    </div>
  );
};

export const WelcomePage: React.FC = () => {
  const { signed_in_full_name, signed_in_username, signed_in_avatar_url } =
    useUserData();
  const { resetAll } = useAuthActions();
  const { workspaces, org } = useWorkspaceAndOrg();

  const handleLogout = () => {
    resetAll();
    window.location.assign(getSignUpUrlWithNext());
  };

  const title = (
    <div className="flex items-center gap-x-2 p-2">
      {org && <OrgLogo org={org} size="medium" />}
      <span>{org?.name}</span>
    </div>
  );

  if (workspaces?.[0]) {
    return <PickWorkspace />;
  }

  return (
    <DashboardContent
      Header={
        <OverviewHeader
          buttonProps={{
            dataLoc: "user-logout",
            title: "Logout",
            variant: "secondary",
            icon: faRightFromBracket,
            onClick: handleLogout,
          }}
          title={title}
        />
      }
    >
      <div className="w-100 p-16">
        <Card
          className="w-100 mx-auto max-w-194 space-y-3 px-4 !pb-10 !pt-10"
          variant="inverted"
        >
          <Card.Content className="px-32 text-center">
            <div className="flex justify-center">
              <Avatar
                size="6xl"
                user={{
                  username: signed_in_username,
                  full_name: signed_in_full_name,
                  avatar_url: signed_in_avatar_url,
                }}
              />
            </div>
            <h3 className="text-gray-800 font-inter-semibold-16px">
              Welcome to Taktile, {signed_in_full_name}
            </h3>
            <p className="mx-auto text-gray-600 font-inter-normal-12px">
              In order to start building and explore the flows created within
              your organization, please ask an administrator to assign you the
              correct permissions and then refresh the page.
            </p>
          </Card.Content>
        </Card>
      </div>
    </DashboardContent>
  );
};
